import React from 'react';
import { Tag, Rate, Progress, Space, Typography, Tooltip } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { 
  TrophyOutlined, 
  WarningOutlined, 
  ThunderboltOutlined,
  FireOutlined 
} from '@ant-design/icons';
import { Exercise } from '../../types';

const { Text } = Typography;

export const getExerciseTableColumns = (): ColumnsType<Exercise> => [
  {
    title: 'Exercise Name',
    dataIndex: 'name',
    key: 'name',
    width: 200,
    fixed: 'left',
    sorter: (a, b) => a.name.localeCompare(b.name),
    render: (text: string, record: Exercise) => (
      <div>
        <Text strong>{text}</Text>
        <br />
        <Text type="secondary" style={{ fontSize: '12px' }}>
          {record.type.charAt(0).toUpperCase() + record.type.slice(1)}
        </Text>
      </div>
    ),
  },
  {
    title: 'Type',
    dataIndex: 'type',
    key: 'type',
    width: 100,
    filters: [
      { text: 'Compound', value: 'compound' },
      { text: 'Isolation', value: 'isolation' },
      { text: 'Cardio', value: 'cardio' },
    ],
    onFilter: (value, record) => record.type === value,
    render: (type: string) => {
      const colorMap = {
        compound: 'blue',
        isolation: 'green',
        cardio: 'orange',
      };
      return <Tag color={colorMap[type as keyof typeof colorMap]}>{type.toUpperCase()}</Tag>;
    },
  },
  {
    title: 'Rep Range',
    dataIndex: 'repRange',
    key: 'repRange',
    width: 100,
    sorter: (a, b) => a.repRange[0] - b.repRange[0],
    render: (repRange: [number, number]) => (
      <Text>{repRange[0]}-{repRange[1]}</Text>
    ),
  },
  {
    title: 'Difficulty',
    dataIndex: 'difficulty',
    key: 'difficulty',
    width: 120,
    sorter: (a, b) => a.difficulty - b.difficulty,
    render: (difficulty: number) => {
      const color = difficulty <= 3 ? 'green' : difficulty <= 6 ? 'orange' : 'red';
      return (
        <div>
          <Progress 
            percent={(difficulty / 10) * 100} 
            size="small" 
            strokeColor={color}
            showInfo={false}
          />
          <Text style={{ fontSize: '12px' }}>{difficulty}/10</Text>
        </div>
      );
    },
  },
  {
    title: 'Rating',
    dataIndex: 'rating',
    key: 'rating',
    width: 120,
    sorter: (a, b) => a.rating - b.rating,
    render: (rating: number) => (
      <Tooltip title={`${rating}/10`}>
        <Rate disabled value={rating / 2} style={{ fontSize: '14px' }} />
      </Tooltip>
    ),
  },
  {
    title: 'Danger',
    dataIndex: 'danger',
    key: 'danger',
    width: 100,
    sorter: (a, b) => a.danger - b.danger,
    render: (danger: number) => {
      const getColor = () => {
        if (danger <= 2) return '#52c41a';
        if (danger <= 4) return '#faad14';
        if (danger <= 6) return '#fa8c16';
        if (danger <= 8) return '#f5222d';
        return '#a8071a';
      };
      
      return (
        <Space>
          <WarningOutlined style={{ color: getColor() }} />
          <Text style={{ color: getColor() }}>{danger}/10</Text>
        </Space>
      );
    },
  },
  {
    title: 'Symmetry',
    dataIndex: 'symmetryMode',
    key: 'symmetryMode',
    width: 120,
    filters: [
      { text: 'Symmetrical', value: 'symmetrical' },
      { text: 'Unilateral', value: 'unilateral' },
      { text: 'Bilateral', value: 'bilateral' },
    ],
    onFilter: (value, record) => record.symmetryMode === value,
    render: (mode: string) => {
      const colorMap = {
        symmetrical: 'blue',
        unilateral: 'purple',
        bilateral: 'cyan',
      };
      return <Tag color={colorMap[mode as keyof typeof colorMap]}>{mode}</Tag>;
    },
  },
  {
    title: 'Primary Muscles',
    key: 'primaryMuscles',
    width: 200,
    render: (_, record: Exercise) => {
      const primaryMuscles = Object.entries(record.muscleActivations)
        .filter(([_, activation]) => activation >= 70)
        .sort(([_, a], [__, b]) => b - a)
        .slice(0, 3)
        .map(([muscle, activation]) => ({ muscle, activation }));

      return (
        <Space direction="vertical" size={2}>
          {primaryMuscles.map(({ muscle, activation }) => (
            <div key={muscle} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Text style={{ fontSize: '12px', minWidth: 60 }}>{muscle}</Text>
              <Progress 
                percent={activation} 
                size="small" 
                showInfo={false}
                strokeColor="#1890ff"
                style={{ flex: 1 }}
              />
              <Text style={{ fontSize: '11px', color: '#666' }}>{activation}%</Text>
            </div>
          ))}
        </Space>
      );
    },
  },
  {
    title: 'Equipment',
    key: 'equipment',
    width: 150,
    render: (_, record: Exercise) => {
      const equipmentItems = Object.keys(record.equipment);
      const displayItems = equipmentItems.slice(0, 2);
      const remainingCount = equipmentItems.length - 2;

      return (
        <Space wrap>
          {displayItems.map(item => (
            <Tag key={item}>{item}</Tag>
          ))}
          {remainingCount > 0 && (
            <Tag>+{remainingCount}</Tag>
          )}
          {equipmentItems.length === 0 && (
            <Text type="secondary" style={{ fontSize: '12px' }}>Bodyweight</Text>
          )}
        </Space>
      );
    },
  },
  {
    title: 'Target Adaptations',
    key: 'adaptations',
    width: 150,
    render: (_, record: Exercise) => {
      const adaptations = Object.entries(record.targetAdaptations)
        .sort(([_, a], [__, b]) => b - a)
        .slice(0, 2);

      return (
        <Space direction="vertical" size={2}>
          {adaptations.map(([adaptation, value]) => (
            <div key={adaptation} style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {adaptation === 'strength' && <ThunderboltOutlined style={{ color: '#fa8c16' }} />}
              {adaptation === 'hypertrophy' && <FireOutlined style={{ color: '#f5222d' }} />}
              {adaptation === 'endurance' && <TrophyOutlined style={{ color: '#52c41a' }} />}
              <Text style={{ fontSize: '12px' }}>{adaptation}</Text>
              <Text style={{ fontSize: '11px', color: '#666' }}>({value}/10)</Text>
            </div>
          ))}
        </Space>
      );
    },
  },
  {
    title: 'Rest Time',
    dataIndex: 'restTime',
    key: 'restTime',
    width: 100,
    sorter: (a, b) => (a.restTime || 0) - (b.restTime || 0),
    render: (restTime?: number) => (
      <Text>{restTime ? `${restTime}s` : 'N/A'}</Text>
    ),
  },
]; 